import { Component, cloneElement } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';

import { actions, selectors } from 'store/newDocumentStore';

/**
 * Used to load relevant documents that are used by pages
 * Loads documents based on 'names' array where each item is one module or 'document type'
 * @class DocumentLoaderContainer
 * @extends {Component}
 *
 * @propTypes
 * @prop {function} dispatch - function used to dispatch actions to redux
 * @prop {element} children - child components
 * @prop {array[string]} names - array of page names
 * @prop {boolean} isLoading - document loading status
 * @prop {array[object]} documents - all loaded documents in an array
 * @prop {array[string]} pending - array of modules that are pending to be loaded
 *
 * @returns Child component with documents loaded
 */
class DocumentLoaderContainer extends Component {
  componentWillMount() {
    const { dispatch, pending } = this.props;
    pending.forEach(name => dispatch(actions.getAllDocumentsByType(name)));
  }

  render() {
    const { names, isLoading, documents, pending, children } = this.props;
    const isPendingLoads = pending.length > 0;
    return !isPendingLoads ? cloneElement(children, { names, isLoading, documents: documents }) : null;
  }
}

DocumentLoaderContainer.propTypes = {
  dispatch: PropTypes.func.isRequired,
  children: PropTypes.element.isRequired,
  names: PropTypes.arrayOf(PropTypes.string).isRequired,
  isLoading: PropTypes.bool.isRequired,
  documents: PropTypes.arrayOf(PropTypes.object),
  pending: PropTypes.arrayOf(PropTypes.string),
};

/**
 * Maps loading status and loaded documents from redux to props.
 *
 * Note: There will often be no need to load anything from backend unless the user refreshes the page
 * or sets the URL directly to some sub page.
 *
 * @param {{ [key: string]: any }} state Redux state root (passed from connect hoc)
 * @param {{ [key: string]: any }} ownProps Props passed directly to the component
 * @returns {{ isLoading: boolean, documents: INeuroDocument[], pending: string[] }}
 */
const mapStateToProps = (state, ownProps) => {
  const pending = [];
  let documents = [];
  let isLoading = false;

  for (const module of ownProps.names) {
    const moduleState = selectors.selectModuleState(module, state.newDocs);
    if (moduleState) {
      if (Array.isArray(moduleState.documents)) {
        documents = documents.concat(moduleState.documents);
      }
      isLoading = isLoading || moduleState.isLoading;
    } else {
      pending.push(module);
    }
  }

  return { isLoading, documents, pending };
};

export default connect(mapStateToProps, null)(DocumentLoaderContainer);
